import React, { useState, useMemo } from 'react';
import { ExpansionPanel, ExpansionPanelContent } from '@progress/kendo-react-layout';
import { Reveal } from '@progress/kendo-react-animation';
import { Checkbox } from '@ui/molecules/inputs/Checkbox';
import snakeToCapitalize from '@utils/functions/snakeToCapitalize';
import { translate } from '@utils/i18n';
import { useAssetTypes } from './hooks/useAssets';
import './AssetTypeSelectorStyles.scss';
const namespace = 'features.settings.ai_inspection_manager.missing_information';
function capitalize(str) {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}
export default function AssetTypeSelector({ selectedAssetTypes, onSelectionChange }) {
    const [searchQuery, setSearchQuery] = useState('');
    const [expandedGroups, setExpandedGroups] = useState([]);
    const { data: assetTypes = [], isLoading, isError } = useAssetTypes();
    const filteredAssetTypes = useMemo(() => {
        if (!searchQuery.trim())
            return assetTypes;
        const query = searchQuery.toLowerCase();
        return assetTypes
            .map((type) => {
            const matchingItems = (type.items || []).filter((item) => item.label.toLowerCase().includes(query));
            if (type.label.toLowerCase().includes(query) || matchingItems.length > 0) {
                return Object.assign(Object.assign({}, type), { items: matchingItems.length > 0 ? matchingItems : type.items });
            }
            return null;
        })
            .filter(Boolean);
    }, [assetTypes, searchQuery]);
    const handleToggleGroup = (groupId) => {
        setExpandedGroups((prev) => (prev.includes(groupId) ? prev.filter((id) => id !== groupId) : [...prev, groupId]));
    };
    const handleToggleAssetType = (value) => {
        onSelectionChange(selectedAssetTypes.includes(value)
            ? selectedAssetTypes.filter((v) => v !== value)
            : [...selectedAssetTypes, value]);
    };
    const handleToggleGroupSelection = (group) => {
        const itemValues = (group.items || []).map((item) => item.value);
        const allSelected = itemValues.every((value) => selectedAssetTypes.includes(value));
        if (allSelected) {
            onSelectionChange(selectedAssetTypes.filter((value) => !itemValues.includes(value)));
        }
        else {
            const newSelection = [...selectedAssetTypes];
            itemValues.forEach((value) => {
                if (!newSelection.includes(value)) {
                    newSelection.push(value);
                }
            });
            onSelectionChange(newSelection);
        }
    };
    if (isLoading) {
        return React.createElement("div", { className: "asset-type-selector__loading" }, translate('loading_assets', { namespace }));
    }
    if (isError) {
        return React.createElement("div", { className: "asset-type-selector__error" }, translate('error_loading_assets', { namespace }));
    }
    return (React.createElement("div", { className: "asset-type-selector" },
        React.createElement("div", { className: "asset-type-selector__search" },
            React.createElement("div", { className: "qmb-input--search", style: { zIndex: 0 } },
                React.createElement("input", { placeholder: translate('search_assets', { namespace }), value: searchQuery, onChange: (e) => setSearchQuery(e.target.value), "aria-label": translate('search_assets', { namespace }) }),
                React.createElement("i", { className: "fa-light fa-search", "aria-hidden": "true" }))),
        React.createElement("div", { className: "asset-type-selector__list" }, filteredAssetTypes.length === 0 ? (React.createElement("div", { className: "asset-type-selector__empty" }, translate('no_assets_found', { namespace }))) : (React.createElement(React.Fragment, null, filteredAssetTypes.map((assetType) => {
            var _a;
            const hasItems = assetType.items && assetType.items.length > 0;
            const itemValues = (assetType.items || []).map((item) => item.value);
            const isGroupExpanded = expandedGroups.includes(assetType.value);
            const allItemsSelected = itemValues.every((value) => selectedAssetTypes.includes(value));
            if (hasItems) {
                return (React.createElement(ExpansionPanel, { key: assetType.value, title: React.createElement(Checkbox, { id: `asset-type-${assetType.value}`, checked: allItemsSelected, onChange: () => handleToggleGroupSelection(assetType), onClick: (e) => {
                            e.stopPropagation();
                        }, label: capitalize(assetType.label), className: "asset-type-selector__group-checkbox" }), expanded: isGroupExpanded, onAction: () => handleToggleGroup(assetType.value) },
                    React.createElement(Reveal, null, isGroupExpanded && (React.createElement(ExpansionPanelContent, null,
                        React.createElement("ul", null, (_a = assetType.items) === null || _a === void 0 ? void 0 : _a.map((item) => (React.createElement("li", { key: item.value },
                            React.createElement(Checkbox, { id: `asset-type-item-${item.value}`, checked: selectedAssetTypes.includes(item.value), onChange: () => handleToggleAssetType(item.value), label: snakeToCapitalize(item.label), className: "asset-type-selector__item-checkbox" }))))))))));
            }
            return (React.createElement("ul", { key: assetType.value, className: "asset-type-selector__standalone-list" },
                React.createElement("li", null,
                    React.createElement(Checkbox, { id: `asset-type-standalone-${assetType.value}`, checked: selectedAssetTypes.includes(assetType.value), onChange: () => handleToggleAssetType(assetType.value), label: capitalize(assetType.label), className: "asset-type-selector__standalone-checkbox" }))));
        }))))));
}
